import React, { useState, useEffect } from 'react';
import {
  FiGlobe,
  FiCheckCircle,
  FiAlertCircle,
  FiLoader,
  FiTrash2,
  FiDownload,
  FiChevronDown,
  FiChevronUp,
  FiClock,
  FiUser,
  FiLink,
  FiRefreshCw,
} from 'react-icons/fi';
import { toast } from 'react-toastify';
import api from '../../utils/api';

const STATUS_LABELS = {
  queued: 'Queued', checking: 'Checking', downloading: 'Downloading',
  cloning: 'Cloning', transcribing: 'Transcribing', translating: 'Translating',
  synthesising: 'Generating', merging: 'Merging', completed: 'Completed', failed: 'Failed',
};

const getVideoUrl = (jobId) => {
  const token = localStorage.getItem('accessToken');
  const base  = api.defaults.baseURL || '/api';
  return `${base}/dubbing/video/${jobId}?token=${encodeURIComponent(token)}`;
};

const JobRow = ({ job, onDelete }) => {
  const [expanded, setExpanded] = useState(false);
  const isDone   = job.status === 'completed';
  const isFailed = job.status === 'failed';
  const label    = job.originalTitle || job.originalFilename || 'Untitled';
  const user     = job.userId;
  const fmtDate  = (d) => new Date(d).toLocaleString();
  const fmtDur   = (s) => { if (!s) return '–'; const m = Math.floor(s/60); return `${m}:${(s%60).toString().padStart(2,'0')}`; };

  return (
    <div className="border border-gray-200 rounded-xl overflow-hidden">
      <button
        onClick={() => setExpanded(v => !v)}
        className="w-full flex items-start gap-3 p-4 text-left hover:bg-gray-50 transition-colors"
      >
        <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5 ${
          isDone ? 'bg-green-100' : isFailed ? 'bg-red-100' : 'bg-blue-100'
        }`}>
          {isDone   ? <FiCheckCircle size={15} className="text-green-600" />  :
           isFailed ? <FiAlertCircle size={15} className="text-red-500" />    :
                      <FiLoader size={15} className="text-blue-500 animate-spin" />}
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium text-gray-800 truncate">{label}</p>
          <div className="flex items-center gap-3 mt-0.5 flex-wrap">
            {user && (
              <span className="flex items-center gap-1 text-xs text-primary-600 font-medium">
                <FiUser size={10} /> {user.name || user.email}
              </span>
            )}
            <span className={`text-xs font-medium ${isDone ? 'text-green-600' : isFailed ? 'text-red-500' : 'text-blue-600'}`}>
              {STATUS_LABELS[job.status] || job.status}
            </span>
            {job.duration > 0 && (
              <span className="flex items-center gap-1 text-xs text-gray-400">
                <FiClock size={10} /> {fmtDur(job.duration)}
              </span>
            )}
            {job.costUSD > 0 && <span className="text-xs text-gray-400">${job.costUSD.toFixed(4)}</span>}
          </div>
          <p className="text-[10px] text-gray-400 mt-0.5">{fmtDate(job.createdAt)}</p>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          <button
            onClick={(e) => { e.stopPropagation(); onDelete(job._id); }}
            className="p-1.5 text-gray-400 hover:text-red-500 transition-colors"
            title="Delete"
          >
            <FiTrash2 size={14} />
          </button>
          {expanded ? <FiChevronUp size={14} className="text-gray-400" /> : <FiChevronDown size={14} className="text-gray-400" />}
        </div>
      </button>

      {expanded && (
        <div className="px-4 pb-4 border-t border-gray-100 pt-4 space-y-3">
          {user && (
            <p className="text-xs text-gray-500">
              <span className="font-medium">User:</span> {user.name} ({user.email})
            </p>
          )}
          {isDone && (
            <div className="space-y-3">
              <video
                src={getVideoUrl(job._id)}
                controls
                className="w-full rounded-xl border border-gray-200 bg-black"
                style={{ maxHeight: 360 }}
              />
              <a
                href={getVideoUrl(job._id)}
                download={`${(label).replace(/[^a-z0-9]/gi, '_')}_telugu.mp4`}
                className="inline-flex items-center gap-2 px-4 py-2 bg-primary-600 text-white text-sm font-medium rounded-lg hover:bg-primary-700 transition-colors"
              >
                <FiDownload size={14} /> Download
              </a>
            </div>
          )}
          {isFailed && (
            <div className="flex items-start gap-2 p-3 bg-red-50 rounded-lg text-sm text-red-700">
              <FiAlertCircle size={14} className="mt-0.5 flex-shrink-0" />
              <span>{job.error || 'Unknown error.'}</span>
            </div>
          )}
          {job.youtubeUrl && (
            <a href={job.youtubeUrl} target="_blank" rel="noreferrer"
              className="flex items-center gap-1 text-xs text-primary-600 hover:underline">
              <FiLink size={11} /> Source video
            </a>
          )}
          {(job.whisperMinutes > 0 || job.ttsCharacters > 0) && (
            <div className="text-xs text-gray-400 space-y-0.5">
              {job.whisperMinutes > 0 && <p>Audio: {job.whisperMinutes.toFixed(1)} min</p>}
              {job.ttsCharacters > 0  && <p>Audio generated: {job.ttsCharacters.toLocaleString()} chars</p>}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export const DubbingHistoryAdmin = () => {
  const [jobs, setJobs]       = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter]   = useState('all');  // all | completed | failed | processing

  const load = async () => {
    setLoading(true);
    try {
      const response = await api.get('/dubbing/admin/history?limit=200');
      setJobs(response.data.data || []);
    } catch (_) {
      toast.error('Failed to load dubbing history.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const handleDelete = async (jobId) => {
    try {
      await api.delete(`/dubbing/${jobId}`);
      setJobs(prev => prev.filter(j => j._id !== jobId));
    } catch (_) {
      toast.error('Failed to delete job.');
    }
  };

  const filtered = jobs.filter(j => {
    if (filter === 'completed')  return j.status === 'completed';
    if (filter === 'failed')     return j.status === 'failed';
    if (filter === 'processing') return !['completed','failed'].includes(j.status);
    return true;
  });

  const totalCost = jobs.filter(j => j.status === 'completed').reduce((s, j) => s + (j.costUSD || 0), 0);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <FiGlobe size={22} className="text-primary-500" />
            Language Change — All Users
          </h1>
          <p className="mt-1 text-sm text-gray-500">
            {jobs.length} total jobs · {jobs.filter(j => j.status === 'completed').length} completed · Total cost: ${totalCost.toFixed(4)}
          </p>
        </div>
        <button
          onClick={load}
          disabled={loading}
          className="flex items-center gap-2 px-4 py-2 text-sm border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50"
        >
          <FiRefreshCw size={14} className={loading ? 'animate-spin' : ''} />
          Refresh
        </button>
      </div>

      {/* Filter tabs */}
      <div className="flex gap-2">
        {[
          { key: 'all',        label: `All (${jobs.length})` },
          { key: 'completed',  label: `Completed (${jobs.filter(j => j.status === 'completed').length})` },
          { key: 'processing', label: `In Progress (${jobs.filter(j => !['completed','failed'].includes(j.status)).length})` },
          { key: 'failed',     label: `Failed (${jobs.filter(j => j.status === 'failed').length})` },
        ].map(({ key, label }) => (
          <button
            key={key}
            onClick={() => setFilter(key)}
            className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
              filter === key
                ? 'bg-primary-100 text-primary-700'
                : 'text-gray-500 hover:bg-gray-100'
            }`}
          >
            {label}
          </button>
        ))}
      </div>

      {/* List */}
      {loading ? (
        <div className="flex items-center justify-center h-48">
          <FiLoader size={24} className="animate-spin text-primary-500" />
        </div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-16 text-gray-400 text-sm">No jobs found.</div>
      ) : (
        <div className="space-y-2">
          {filtered.map(job => (
            <JobRow key={job._id} job={job} onDelete={handleDelete} />
          ))}
        </div>
      )}
    </div>
  );
};

export default DubbingHistoryAdmin;
